import {toastDefaultConfig} from '@/shared/config/toastConfig'
import {S3ProviderListOutput, StorageGetOutput} from '@aws-amplify/storage'
import {API, Auth, Storage} from 'aws-amplify'
import {Id, toast} from 'react-toastify'
import {
  apiCallWithAuthCheck,
  handleAuthError,
  refreshSessionIfPolicyAllows,
} from '../../auth/core/AuthErrorInterceptor'
import {
  Associate2PropsResponse,
  BalanceProps,
  dbItem,
  FsProps,
  HistoryProps,
  Job,
  JobHistory,
  RenameProps,
  shareOutput,
  SubscriptionDetails,
  UsageProps,
} from '../../../../shared/utils'
import {getLogger} from '../../../../shared/utils/logUtils'
import {guardLeave} from '../../../../shared/utils/leaveGuard'
import {bucket} from '../../../../aws-config'
import {fakeTagsForFile} from '../../../../mocks/fakeOntologyTags'
import type {FileTags} from '../../../../shared/utils/ontology/tags'
import {
  failUpload,
  finishUpload,
  reportUploadProgress,
  trackUpload,
  uploadDestination,
} from './uploadTracker'
import type {
  DismissImportsRequest,
  DismissImportsResponse,
  ImportListResponse,
  ImportRecord,
  ImportSourceType,
} from './importModels'

const apiName: string = process.env.REACT_APP_API_NAME || 'JobService'

const ensureAuth = async () => {
  try {
    await Auth.currentSession()
  } catch (error) {
    const refreshed = await refreshSessionIfPolicyAllows()
    if (refreshed) {
      try {
        await Auth.currentSession()
        return
      } catch {
        // fall through to modal + throw original path
      }
    }
    handleAuthError(error)
    throw error
  }
}

const storageCallWithAuthCheck = async <T>(storageCall: () => Promise<T>): Promise<T> => {
  await ensureAuth()
  try {
    return await storageCall()
  } catch (error) {
    handleAuthError(error)
    throw error
  }
}
interface QueryProps {
  limit?: number
  lastKey?: string
  filePrefix?: string
}

const getFiles = async (identityId: string): Promise<S3ProviderListOutput> => {
  if (!identityId) {
    throw new Error('identityId not provided')
  }
  const fileListOutput: S3ProviderListOutput = await Storage.list(``, {
    customPrefix: {public: '', private: '', protected: ''},
    level: 'private',
    identityId: identityId,
  })

  return fileListOutput
}

const getFilesV2 = async (
  filePrefix?: string,
  lastKey?: string,
  limit?: number
): Promise<dbItem[] | undefined> => {
  return apiCallWithAuthCheck(async () => {
    let files: dbItem[] | undefined = undefined
    const queryParams: QueryProps = {limit: limit, lastKey: lastKey, filePrefix: filePrefix}
    const getFilesResponse = await API.get(apiName, 'storage/files', {
      queryStringParameters: queryParams,
    })
    files = getFilesResponse.files.items
    let lastKeyResponse = getFilesResponse.files.lastKey
    while (lastKeyResponse) {
      const response = await API.get(apiName, 'storage/files', {
        queryStringParameters: {...queryParams, lastKey: lastKeyResponse},
      })
      //add new results to files
      files = files ? files.concat(response.files.items) : response.files.items
      //update lastKeyResponse for next request
      lastKeyResponse = response.files.lastKey
    }

    // Dev-only: stand in for the not-yet-deployed file-ontology Lambda by faking the
    // `tags` it would write onto each row (see src/mocks/fakeOntologyTags.ts). Only fills
    // in rows the backend didn't already tag, so it's a no-op once the Lambda ships. Gated
    // by REACT_APP_FAKE_ONTOLOGY_TAGS (set only in environments/.local-observer.env).
    if (process.env.REACT_APP_FAKE_ONTOLOGY_TAGS === 'true' && files) {
      files = files.map((item) => (item.tags ? item : {...item, tags: fakeTagsForFile(item.file)}))
    }

    return files
  })
}

const downloadFile = async (fileName: string, isMultiple: boolean): Promise<string | Blob> => {
  if (!fileName) {
    throw new Error('fileName not provided')
  }

  let t: Id | undefined = undefined
  let file: StorageGetOutput<any> | string | undefined = undefined
  try {
    file = await Storage.get(fileName, {
      customPrefix: {public: '', private: '', protected: ''},
      level: 'private',
      download: isMultiple,
      progressCallback: (p_1) => {
        const progress = p_1.loaded / p_1.total
        if (t === undefined) {
          t = toast(`Downloading ${fileName.substring(0, 50) + '...'}`, {
            ...toastDefaultConfig,
            hideProgressBar: false,
            progress,
          })
        } else {
          if (toast.isActive(t)) {
            toast.update(t, {
              progress,
            })
          }
        }
      },
    })
  } catch (error) {
    getLogger().error(error)
  }
  if (t) {
    toast.done(t)
    toast.dismiss(t)
  }
  //@ts-ignore
  const response: string | Blob = typeof file === 'string' ? file : file.Body
  return response
}

const removeFile = async (fileName: string): Promise<string> => {
  if (!fileName) {
    throw new Error('fileName not provided')
  }

  return storageCallWithAuthCheck(async () => {
    await Storage.remove(fileName, {
      customPrefix: {public: '', private: '', protected: ''},
      level: 'private',
    })
    return fileName
  })
}

const uploadFile = async (filePath: string, fileToUpload?: File) => {
  if (!filePath) {
    throw new Error('filePath not provided')
  }
  return storageCallWithAuthCheck(async () => {
    const filename = filePath.split('/').pop() || ''
    let t: Id | undefined = undefined
    // Assigned before put() runs (put is only called below), read by its progress callback.
    let uploadId: string | undefined
    const put = () =>
      Storage.put(fileToUpload ? filePath : `${filePath}/`, fileToUpload || '', {
        level: 'private',
        customPrefix: {public: '', private: '', protected: ''},
        contentType: fileToUpload?.type || '',
        progressCallback: (p_1) => {
          if (uploadId) reportUploadProgress(uploadId, p_1.loaded)
          const progress = p_1.loaded / p_1.total
          if (t === undefined) {
            t = toast(`Uploading ${filename.substring(0, 50) + '...'}`, {
              ...toastDefaultConfig,
              hideProgressBar: false,
              progress,
            })
          } else {
            toast.update(t, {
              progress,
            })
          }
        },
      })
    if (!fileToUpload) return put() // an empty folder marker: instant, not a transfer

    uploadId = trackUpload({
      name: filename,
      destinationPath: uploadDestination(filePath),
      size: fileToUpload.size,
    })
    try {
      // Closing the tab aborts the upload, so ask first.
      const res = await guardLeave(put)
      finishUpload(uploadId)
      return res
    } catch (error: unknown) {
      failUpload(uploadId, error instanceof Error ? error.message : 'Upload failed')
      throw error
    } finally {
      if (t) {
        toast.done(t)
      }
    }
  })
}

const shareFiles = async (fileNames: string[], identityId: string) => {
  return apiCallWithAuthCheck(async () => {
    if (!identityId) {
      getLogger().error('identityId not provided')
      console.warn('identityId not provided')
    }
    const s3Objects = fileNames.map((fileName) => identityId + '/' + fileName)
    let response: shareOutput = {publicUrls: []}
    try {
      response = await API.post(apiName, 'storage/shareLink', {body: {s3Objects: s3Objects}})
    } catch (error: any) {
      getLogger().error('shareOutput', error.message)
      throw error
    }

    return response
  })
}
const unshareFiles = (paths: string[], identityId: string) => {
  return apiCallWithAuthCheck(async () => {
    if (!identityId) {
      getLogger().error('identityId not provided')
      console.warn('identityId not provided')
    }
    const s3Objects = paths.map((path) => identityId + '/' + path)

    const response = await API.post(apiName, 'storage/unshare', {body: {s3Objects}})
    return response
  })
}

const getHistory = async (props: HistoryProps): Promise<Job[]> => {
  return apiCallWithAuthCheck(async () => {
    let response: JobHistory = {}
    response = await API.post(apiName, 'session/history', {body: props})
    return response.items || []
  })
}

const getUsage = async (cancel?: boolean) => {
  return apiCallWithAuthCheck(async () => {
    let response: UsageProps = {}
    if (apiName) {
      let promise = API.get(apiName, 'storage/usage', {})
      try {
        response = await promise
        if (cancel) API.cancel(promise, 'getUsage aborted')
      } catch (error: any) {
        getLogger().error(error.message)
        throw error
      }
    }
    return response.totalSize
  })
}

const getBalance = async (skey: string, cancel?: boolean) => {
  return apiCallWithAuthCheck(async () => {
    let response: BalanceProps = {}
    if (apiName) {
      const queryParams = {userId: skey}

      let promise = API.get(apiName, 'balance', {
        queryStringParameters: queryParams,
      })
      try {
        response = await promise
        if (cancel) API.cancel(promise, 'getBalance aborted')
      } catch (error: any) {
        getLogger().error(error.message)
        throw error
      }
    }
    return response.userAvaliableUnits
  })
}

const getSubscriptionId = async () => {
  return apiCallWithAuthCheck(async () => {
    let response: Associate2PropsResponse = {}
    response = await API.get(apiName, 'associate2', {})

    return response.subscriptionId
  })
}

/**
 * Switch the currently active subscription for the authenticated user.
 * The backend rewrites the caller's USER#/USER# billing row to point at the
 * subscription identified by `subscriptionKey` (full Susy key), and returns
 * `{status: "ok"}` on success. Callers must refetch subscription-dependent
 * state afterwards (id, details, members, balance).
 */
const setActiveSubscription = async (subscriptionKey: string): Promise<void> => {
  return apiCallWithAuthCheck(async () => {
    await API.post(apiName, 'associate2', {body: {subscriptionKey}})
  })
}

const renameFile = (props: RenameProps, identityId: string) => {
  return apiCallWithAuthCheck(async () => {
    const source: string = identityId + '/' + props.source
    return API.post(apiName, 'storage/rename', {body: {source, destination: props.destination}})
  })
}

const moveFiles = (props: FsProps, identityId: string) => {
  return apiCallWithAuthCheck(async () => {
    if (Array.isArray(props.source)) {
      const source = props.source.map((path) => {
        return identityId + '/' + path
      })
      return API.post(apiName, 'storage/move', {body: {source, destination: props.destination}})
    }
  })
}

const copyFiles = (props: FsProps, identityId: string) => {
  return apiCallWithAuthCheck(async () => {
    const source = props.source.map((path) => {
      return identityId + '/' + path
    })
    return API.post(apiName, 'storage/copy', {body: {source, destination: props.destination}})
  })
}

/** The signed-in user's identity id: the `<identityId>/` prefix of every own storage key. */
const getIdentityId = async (): Promise<string> =>
  Auth.currentAuthenticatedUser()
    .then((user) => user.attributes['custom:identityId'])
    .catch((error) => {
      console.error('Error getting the current authenticated user', error)
      // Create a proper error object that our interceptor can recognize
      const authError = new Error('The user is not authenticated')
      authError.name = 'NotAuthorizedException'
      // Add code property using type assertion
      ;(authError as any).code = 'NotAuthorizedException'
      throw authError
    })

const getFileS3Url = async (fileName: string) => {
  return apiCallWithAuthCheck(async () => {
    const poolId = await getIdentityId()
    return encodeURI(`s3://${bucket}/${poolId}/${fileName}`)
  })
}

/**
 * Ontology tags of one own file (path relative to the user's root), read from its `storage/files`
 * row. For callers that only have a path, such as a link in a chat answer, where the Files listing
 * would have handed over the row with its tags. Undefined when the row is missing or untagged.
 */
const getFileTags = async (fileName: string): Promise<FileTags | undefined> => {
  const identityId = await apiCallWithAuthCheck(getIdentityId)
  // The full key as prefix lists the file itself plus any sibling it prefixes (`a.csv.bak`).
  const rows = await getFilesV2(`${identityId}/${fileName}`)
  return rows?.find((row) => row.file === fileName)?.tags
}

/* ---------- External Import (S3 / HTTPS / FTP) ---------- */

export type {ImportSourceType}

export interface BrowseItem {
  name: string
  path: string
  size?: number
  lastModified?: string
  isDir: boolean
}

interface BrowseExternalParams {
  sourceType?: ImportSourceType
  // S3
  sourceBucket?: string
  sourcePrefix?: string
  accessKeyId?: string
  secretAccessKey?: string
  continuationToken?: string
  maxKeys?: number
  requesterPays?: boolean
  // FTP
  ftpHost?: string
  ftpPort?: number
  ftpUser?: string
  ftpPassword?: string
  ftpPath?: string
  ftpSecure?: boolean
}

interface BrowseExternalResponse {
  items?: BrowseItem[]
  continuationToken?: string | null
  isTruncated?: boolean
  error?: string
}

const browseExternalS3 = async (params: BrowseExternalParams): Promise<BrowseExternalResponse> => {
  return apiCallWithAuthCheck(async () => {
    return API.post(apiName, 'storage/import/browse', {body: params})
  })
}

interface ImportExternalParams {
  sourceType?: ImportSourceType
  // S3
  sourceBucket?: string
  sourceKeys?: string[]
  accessKeyId?: string
  secretAccessKey?: string
  requesterPays?: boolean
  // HTTPS
  urls?: string[]
  // FTP
  ftpHost?: string
  ftpPort?: number
  ftpUser?: string
  ftpPassword?: string
  ftpPaths?: string[]
  ftpSecure?: boolean
  // Common
  destinationPrefix: string
}

interface ImportExternalResponse {
  status?: 'started'
  /** The import's status record (see `listImports`); set when status is 'started'. */
  importId?: string
  sourceType?: string
  totalCopied?: number
  totalFailed?: number
  results?: Array<{key: string; status: string; error?: string; size?: number}>
  error?: string
}

const importFromS3 = async (params: ImportExternalParams): Promise<ImportExternalResponse> => {
  return apiCallWithAuthCheck(async () => {
    return API.post(apiName, 'storage/import', {body: params})
  })
}

/** Hides finished imports from the caller's list (`POST storage/import/dismiss`). */
const dismissImports = async (request: DismissImportsRequest): Promise<DismissImportsResponse> => {
  return apiCallWithAuthCheck(async () => {
    return API.post(apiName, 'storage/import/dismiss', {body: request})
  })
}

/** The caller's imports of the last 7 days, newest first, without dismissed ones. */
const listImports = async (limit?: number): Promise<ImportRecord[]> => {
  return apiCallWithAuthCheck(async () => {
    const response: ImportListResponse = await API.get(apiName, 'storage/import', {
      queryStringParameters: limit ? {limit: String(limit)} : undefined,
    })
    return response?.imports ?? []
  })
}

// --- Subscription Details (proxied via JobService Lambda) ---

const getSubscriptionDetails = async (): Promise<SubscriptionDetails> => {
  return apiCallWithAuthCheck(async () => {
    return API.get(apiName, 'subscription-details', {})
  })
}

export {
  downloadFile,
  getFiles,
  getFilesV2,
  getHistory,
  getSubscriptionId,
  setActiveSubscription,
  getSubscriptionDetails,
  getUsage,
  getBalance,
  removeFile,
  moveFiles,
  shareFiles,
  unshareFiles,
  uploadFile,
  copyFiles,
  renameFile,
  getFileS3Url,
  getFileTags,
  browseExternalS3,
  importFromS3,
  listImports,
  dismissImports,
}
